/**
 * Domain tenancy: satu-satunya domain yang berdiri DI LUAR tenant.
 *
 * Seluruh domain lain bekerja di dalam satu tenant dan tidak pernah menyebutnya —
 * backend yang menentukannya dari token. Yang di sini kebalikannya: ia mendaftar,
 * membuat, dan menyambungkan tenant, jadi tenantnya justru jadi argumen.
 *
 * Hanya `smova_admin` yang boleh memanggilnya. Klien ini TIDAK ikut memeriksanya —
 * kalau ia menyaring sendiri, pemakaian yang salah jadi gagal dengan cara berbeda di
 * dua sisi, dan yang memperbaikinya harus menebak sisi mana yang benar.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, kueri } from '../../shared/api/client'

export const kunci = {
  tenant: () => ['tenant', 'daftar'],
  koneksi: (id) => ['tenant', 'koneksi', id],
  orang: (id) => ['tenant', 'orang', id],
}

export function useDaftarTenant() {
  return useQuery({
    queryKey: kunci.tenant(),
    queryFn: () => api.get('/tenants'),
  })
}

/**
 * Tenant yang sedang dibuka — terutama ZONANYA.
 *
 * `tenantId` diisi HANYA oleh operator Smova, yang tidak punya tenant sendiri dan
 * wajib menyebutnya lewat `X-Tenant-Id`. Staf tenant memanggilnya tanpa argumen:
 * backend menolak 403 kalau mereka menyebut tenant (ADR-045).
 *
 * `staleTime: Infinity` — zona tenant berubah lewat formulir admin, dan yang berubah
 * berlaku pada pemuatan berikutnya. Menyegarkannya tiap fokus jendela menambah
 * permintaan di tiap layar tanpa mengubah satu pun angka di layar.
 *
 * `pilihan` menimpa bawaan itu untuk SATU pengamat. Kotak tenant rancang ulang
 * menampilkan MODE tenant, yang diubah operator kapan saja; pengamat itu minta datanya
 * basi sesudah semenit dan diambil ulang saat jendela difokus. Pemanggil lain tidak ikut.
 */
export function useTenantIni(tenantId, aktif = true, pilihan = {}) {
  return useQuery({
    queryKey: ['tenant', 'ini', tenantId ?? 'saya'],
    queryFn: () => api.get('/dashboard/tenant', tenantId ? { tenant: tenantId } : undefined),
    enabled: aktif,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    ...pilihan,
  })
}

/**
 * Pagu belanja bulan ini — BACA SAJA.
 *
 * Angkanya sudah menggerakkan sistem sejak ADR-064: `pagu_menahan()` menghentikan
 * gambar, video, lalu drafting. Yang belum ada cuma cara melihatnya, jadi klien berhenti
 * dilayani tanpa peringatan dan yang pertama tahu orang yang menekan tombol lalu gagal.
 */
export function usePagu() {
  return useQuery({
    queryKey: ['ta', 'pagu'],
    queryFn: () => api.get('/dashboard/pagu'),
  })
}

/**
 * Terbitkan kode `/join` untuk SATU orang.
 *
 * Memanggil berulang untuk orang yang sama tidak menumpuk kode hidup — backend
 * mengembalikan undangan aktif apa adanya dan menandainya `sudah_ada`. Jadi tombolnya
 * aman ditekan dua kali, dan layar bisa mengatakan "ini kode lama" alih-alih berpura-pura
 * baru menerbitkannya.
 */
export function useTerbitkanKode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (webUserId) => api.post('/dashboard/invites', { web_user_id: webUserId }),
    // Daftar orang membawa kode yang masih berlaku, jadi ia BASI begitu kode baru
    // terbit. Tanpa ini, meninggalkan layar lalu kembali menampilkan keadaan sebelum
    // tombol ditekan — persis cacat yang baru saja ditutup, cuma lewat pintu lain.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ta', 'orang'] }),
  })
}

/** Roster: siapa yang bisa ditugaskan meeting, dan seberapa sibuk ia. */
export function useRoster() {
  return useQuery({
    queryKey: ['ta', 'roster'],
    queryFn: () => api.get('/dashboard/roster'),
  })
}

export function useUbahRoster() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, boleh_ditugaskan }) =>
      api.patch(`/dashboard/roster/${id}`, { boleh_ditugaskan }),
    // Daftarnya dimuat ulang, bukan ditambal dari balasan: PATCH sengaja tidak
    // menghitung ulang `tanggal_diblokir` dan `meeting_bulan_ini` — menghitungnya dua
    // kali menambah dua kueri untuk angka yang tidak berubah oleh aksi ini. Daftar orang
    // membawa nilai yang sama, jadi ikut dimuat ulang.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ta', 'roster'] })
      qc.invalidateQueries({ queryKey: ['ta', 'orang'] })
    },
  })
}

/**
 * Uji ambil — MURAH: satu embedding, satu pencarian vektor, nol model bahasa.
 * Boleh dijalankan berkali-kali, dan memang harus: yang dicari justru pertanyaan yang
 * TIDAK menemukan apa-apa.
 */
export function useUjiAmbil() {
  return useMutation({
    mutationFn: (pertanyaan) =>
      api.post('/dashboard/pengetahuan/uji-ambil', { pertanyaan }),
  })
}

/** Uji percakapan — MAHAL: memanggil model sungguhan dan menagih biaya. */
export function useUjiPercakapan() {
  return useMutation({
    mutationFn: (pertanyaan) =>
      api.post('/dashboard/pengetahuan/uji-percakapan', { pertanyaan }),
  })
}

/**
 * Nyalakan/matikan fitur SEBUAH TENANT — wewenang operator Smova.
 *
 * Tombolnya dulu ada di Tenant Admin dan tidak ada di sini. Fitur adalah yang kita jual;
 * klien yang menyalakannya sendiri berarti mengambil dari katalog tanpa membelinya.
 * Backend kini menolak `enabled` dari siapa pun selain `smova_admin` (403).
 */
export function useUbahFiturTenantOleh(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, enabled }) =>
      api.patch(`/dashboard/fitur/${key}`, { enabled }, { tenant: tenantId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adm', 'fitur', tenantId] })
      // Ikhtisar menghitung berapa fitur yang mati — ia jadi basi begitu satu diubah.
      qc.invalidateQueries({ queryKey: kunci.koneksi(tenantId) })
    },
  })
}

/** Model yang dipilih SEBUAH tenant — dilihat operator, tanpa masuk ke dalam tenantnya. */
export function useModelTenantOleh(tenantId) {
  return useQuery({
    queryKey: ['adm', 'model', tenantId],
    queryFn: () => api.get('/dashboard/model', { tenant: tenantId }),
    enabled: !!tenantId,
  })
}

/** Pagu belanja sebuah tenant — baca-saja, lewat rute yang sama dengan layar tenant. */
export function usePaguOleh(tenantId) {
  return useQuery({
    queryKey: ['adm', 'pagu', tenantId],
    queryFn: () => api.get('/dashboard/pagu', { tenant: tenantId }),
    enabled: !!tenantId,
  })
}

export function useKoneksi(tenantId) {
  return useQuery({
    queryKey: kunci.koneksi(tenantId),
    queryFn: () => api.get(`/tenants/${tenantId}/koneksi`),
    enabled: !!tenantId,
  })
}

/**
 * Orang di sebuah tenant — memakai ULANG rute tenant biasa lewat `X-Tenant-Id`.
 *
 * Tidak ada `/tenants/{id}/orang`, dan itu disengaja: sejak ADR-045 operator Smova
 * boleh memakai setiap rute ber-tenant untuk tenant mana pun. Membuat rute kembar
 * khusus operator berarti dua tempat yang menjawab pertanyaan sama dan akan
 * menyimpang.
 */
/**
 * Mutasi orang di sebuah tenant — dipakai konsol operator.
 *
 * Keempatnya membatalkan cache daftar orang tenant itu, bukan seluruh cache: operator
 * bisa membuka dua tenant berurutan, dan membatalkan semuanya akan memuat ulang daftar
 * yang tidak berubah.
 *
 * `tenant` dikirim sebagai `X-Tenant-Id` (lewat `kueri`/opsi `tenant`), karena operator
 * berdiri DI LUAR semua tenant dan harus menyebut yang mana (ADR-045).
 */
function mutasiOrang(tenantId, jalan) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: jalan,
    onSuccess: () => qc.invalidateQueries({ queryKey: kunci.orang(tenantId) }),
  })
}

export function useBuatOrang(tenantId) {
  return mutasiOrang(tenantId, (body) =>
    api.post('/dashboard/web-users', body, { tenant: tenantId }))
}

export function useUbahPeran(tenantId) {
  return mutasiOrang(tenantId, ({ id, role }) =>
    api.patch(`/dashboard/web-users/${id}/peran`, { role }, { tenant: tenantId }))
}

/**
 * Isi atau ubah nama seorang anggota tim. Tanpa `tenantId`: Tenant Admin di tenantnya sendiri
 * (layar Tim); dengan `tenantId`: operator di Detail tenant.
 *
 * Yang dimuat ulang semua daftar yang menyebut orang: daftar orangnya, roster, dan pemilih
 * peserta meeting. Nama peserta di meeting dan prospek dibaca server saat diminta.
 */
export function useUbahNamaOrang(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }) =>
      api.patch(`/dashboard/web-users/${id}/nama`, { name }, tenantId ? { tenant: tenantId } : undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantId ? kunci.orang(tenantId) : ['ta', 'orang'] })
      qc.invalidateQueries({ queryKey: ['ta', 'roster'] })
      qc.invalidateQueries({ queryKey: ['tim'] })
    },
  })
}

export function useTogglePengguna(tenantId) {
  return mutasiOrang(tenantId, (id) =>
    api.patch(`/dashboard/web-users/${id}/toggle`, undefined, { tenant: tenantId }))
}

export function useTautTelegram(tenantId) {
  return mutasiOrang(tenantId, ({ id, telegram_user_id, telegram_username }) =>
    api.patch(`/dashboard/web-users/${id}/telegram`,
              { telegram_user_id: telegram_user_id || null,
                telegram_username: telegram_username || null },
              { tenant: tenantId }))
}

/** Aktifkan atau nonaktifkan satu akun, dengan nilai yang disebut (saklar di detail tenant). */
export function useAktifkanOrang(tenantId) {
  return mutasiOrang(tenantId, ({ id, is_active }) =>
    api.patch(`/dashboard/web-users/${id}/toggle`, { is_active }, { tenant: tenantId }))
}

/** Persona dan jadwal satu tenant, dibaca operator untuk kartu Konfigurasi klien. */
export function usePersonaOleh(tenantId) {
  return useQuery({
    queryKey: ['adm', 'persona', tenantId],
    queryFn: () => api.get('/dashboard/persona', { tenant: tenantId }),
    enabled: !!tenantId,
    retry: false,
  })
}

export function useJadwalOleh(tenantId) {
  return useQuery({
    queryKey: ['adm', 'jadwal', tenantId],
    queryFn: () => api.get('/dashboard/jadwal', { tenant: tenantId }),
    enabled: !!tenantId,
  })
}

export function useOrangTenant(tenantId) {
  return useQuery({
    queryKey: kunci.orang(tenantId),
    queryFn: () => api.get('/dashboard/web-users', { tenant: tenantId }),
    enabled: !!tenantId,
  })
}

export function useBuatTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post('/tenants', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: kunci.tenant() }),
  })
}

/** Pasang, ubah, atau cabut pagu belanja AI satu tenant. Hanya operator. */
export function useUbahPaguTenant(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.put(`/tenants/${tenantId}/pagu`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kunci.tenant() })
      qc.invalidateQueries({ queryKey: ['adm', 'pagu', tenantId] })
    },
  })
}

export function useUbahTenant(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.patch(`/tenants/${tenantId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kunci.tenant() })
      qc.invalidateQueries({ queryKey: kunci.koneksi(tenantId) })
    },
  })
}

export function useDaftarkanAkun(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post(`/tenants/${tenantId}/akun`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: kunci.koneksi(tenantId) }),
  })
}

export function useUbahAkun(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ akunId, aktif }) =>
      api.patch(`/tenants/${tenantId}/akun/${akunId}`, { aktif }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kunci.koneksi(tenantId) }),
  })
}

/**
 * Memuat token Meta. SEKALI TULIS — tidak ada rute yang mengembalikannya, jadi
 * layar tidak pernah bisa menampilkan ulang apa yang barusan diketik. Nilainya juga
 * TIDAK disimpan di state mana pun setelah terkirim (lihat layar Koneksi).
 */
export function useMuatKredensial(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    /**
     * Menerima string ATAU objek, dan itu bukan kelonggaran: pemanggil lama mengirim
     * tokennya saja (`provider` jatuh ke bawaan 'meta' di backend), sementara alur
     * "Tambah akun" menyebut penyedianya karena platform berikutnya belum tentu meta.
     *
     * Kalau tanda tangannya diganti begitu saja, pemanggil lama akan mengirim
     * `{ nilai: "<token>" }` yang bentuknya benar dan ISINYA salah — token tersimpan
     * sebagai objek, dan gagalnya baru muncul saat adapter memakainya.
     */
    mutationFn: (arg) => {
      const body = typeof arg === 'string'
        ? { nilai: arg }
        : {
            nilai: arg.nilai,
            ...(arg.provider ? { provider: arg.provider } : {}),
            // Kosong = tidak diketahui, dan itu dikirim sebagai tidak-ada, bukan null
            // string. Backend membedakan "tidak diisi" dari "tidak kedaluwarsa".
            ...(arg.expires_at ? { expires_at: arg.expires_at } : {}),
          }
      return api.post(`/tenants/${tenantId}/kredensial`, body)
    },
    // TIGA kueri, bukan satu. Sesudah rotasi, yang berubah bukan cuma ringkasan koneksi:
    // daftar kredensial menampilkan baris lama jadi 'rotated', dan peta platform
    // menentukan apakah tombol "Tambah akun" masih meminta token. Melewatkan salah
    // satunya membuat layar menampilkan keadaan sebelum rotasi sampai orangnya memuat ulang.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kunci.koneksi(tenantId) })
      qc.invalidateQueries({ queryKey: ['adm', 'kredensial', tenantId] })
      qc.invalidateQueries({ queryKey: ['adm', 'platform', tenantId] })
    },
  })
}

/** Setelan sebuah tenant — juga rute tenant biasa lewat `X-Tenant-Id`. */
export function useSetelanTenant(tenantId) {
  return useQuery({
    queryKey: ['tenant', 'setelan', tenantId],
    queryFn: () => api.get('/dashboard/settings', { tenant: tenantId }),
    enabled: !!tenantId,
  })
}

export function useUbahSetelanTenant(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.patch('/dashboard/settings', body, { tenant: tenantId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', 'setelan', tenantId] })
      qc.invalidateQueries({ queryKey: kunci.koneksi(tenantId) })
    },
  })
}

/**
 * Jalur masuk — GLOBAL, bukan per tenant.
 *
 * Kejadian berstatus `tanpa_tenant` punya `tenant_id NULL` menurut definisinya, jadi
 * ia tidak punya tempat di rute ber-tenant. `saring.tenant` menerima UUID ATAU string
 * `'tanpa'`, dan yang kedua itulah penyaring paling berguna di layar diagnosa.
 */
export function useJalurMasuk(saring = {}, { tahan = false } = {}) {
  return useQuery({
    queryKey: ['jalur-masuk', saring],
    queryFn: () => api.get(`/jalur-masuk${kueri(saring)}`),
    // `tahan`: daftar lama tetap tampil selagi saringan baru dimuat, supaya tombol yang baru
    // ditekan tidak hilang dari bawah jari.
    placeholderData: tahan ? (lama) => lama : undefined,
    // Layar diagnosa dibuka justru saat ada yang sedang terjadi, jadi basi lima detik
    // pun terasa. Lebih pendek dari bawaan 30 detik.
    staleTime: 5_000,
  })
}

/**
 * Catatan jalan (ADR-088): GLOBAL, untuk operator.
 *
 * `saring`: `tenant`, `status`, `jenis`, `alur`, `jam` (hanya yang mulai dalam sekian jam
 * terakhir), `limit`. `per_status` di jawabannya TIDAK ikut tersaring status, jadi satu
 * permintaan cukup untuk daftar dan hitungan tombol saringnya.
 */
export function useJejakAlur(saring = {}, { tahan = false } = {}) {
  return useQuery({
    queryKey: ['jejak-alur', saring],
    queryFn: () => api.get(`/jejak-alur${kueri(saring)}`),
    // `tahan`: daftar yang sudah tampil tetap ada selagi saringan atau batas baru dimuat.
    placeholderData: tahan ? (sebelumnya) => sebelumnya : undefined,
    // Sama dengan jalur masuk: layar ini dibuka saat ada yang sedang terjadi.
    staleTime: 5_000,
  })
}

/**
 * Satu kejadian BESERTA payload mentahnya.
 *
 * `enabled` sengaja dikendalikan pemanggil: membukanya DICATAT di `audit_log`
 * (ADR-052), jadi ia tidak boleh terpanggil hanya karena komponennya ter-render.
 * Yang memicunya harus tindakan orang, bukan siklus hidup React.
 */
export function useKejadian(id) {
  return useQuery({
    queryKey: ['jalur-masuk', 'kejadian', id],
    queryFn: () => api.get(`/jalur-masuk/${id}`),
    enabled: !!id,
    // Tidak disegarkan sendiri: tiap pengambilan ulang menambah satu baris audit,
    // dan payload mentah tidak berubah setelah tersimpan.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

/**
 * Pemakaian satu tenant. Rutenya SUDAH ADA sejak sebelum konsol operator —
 * `/dashboard/usage`, ber-scope tenant, dijaga `tenant_admin` — jadi operator
 * memakainya lewat `X-Tenant-Id` seperti rute tenant lainnya.
 *
 * `tenantId` WAJIB, dan itu bukan kelalaian rancangan: `get_tenant_id` menolak
 * melayani `smova_admin` tanpa header itu (ADR-045). Tidak ada "semua tenant" di
 * sini, dan menjumlahkan lintas klien memang bukan pertanyaan yang dijawab halaman
 * ini.
 */
export function usePemakaian(tenantId, days = 30) {
  return useQuery({
    queryKey: ['pemakaian', tenantId, days],
    queryFn: () => api.get(`/dashboard/usage?days=${days}`, { tenant: tenantId }),
    enabled: !!tenantId,
  })
}

/* ── Katalog: data induk, di luar semua tenant (ADR-054 tingkat 1) ─────────── */

export function useKatalogFitur() {
  return useQuery({
    queryKey: ['katalog', 'fitur'],
    queryFn: () => api.get('/katalog/fitur'),
    // Disemai migrasi dan hanya berubah lewat migrasi berikutnya.
    staleTime: Infinity,
  })
}

export function useKatalogModel() {
  return useQuery({
    queryKey: ['katalog', 'model'],
    queryFn: () => api.get('/katalog/model'),
  })
}

export function useTambahModel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post('/katalog/model', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['katalog', 'model'] }),
  })
}

export function useUbahModel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...sisa }) => api.patch(`/katalog/model/${id}`, sisa),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['katalog', 'model'] }),
  })
}

/* ── Tingkat 2: sisi tenant dari katalog (ADR-054) ────────────────────────── */

export function useFiturTenant() {
  return useQuery({ queryKey: ['ta', 'fitur'], queryFn: () => api.get('/dashboard/fitur') })
}

export function useUbahFiturTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, ...sisa }) => api.patch(`/dashboard/fitur/${key}`, sisa),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ta', 'fitur'] }),
  })
}

export function useModelTenant() {
  return useQuery({ queryKey: ['ta', 'model'], queryFn: () => api.get('/dashboard/model') })
}

/**
 * Ganti sandi SENDIRI, untuk semua peran. Sandi lama wajib cocok; yang salah dijawab 400
 * dengan kalimatnya, bukan 401, supaya orangnya tidak dibuang ke halaman masuk.
 */
export function useGantiSandi() {
  return useMutation({
    mutationFn: ({ lama, baru }) => api.patch('/dashboard/me/sandi', { sandi_lama: lama, sandi_baru: baru }),
  })
}

/**
 * Orang yang sedang masuk menurut server, dengan NAMANYA (`name`, null kalau belum diisi).
 * Dibaca dialog "Ubah nama" di menu akun operator, hanya selagi dialognya terbuka; sesi di
 * peramban tidak menyimpan nama itu.
 */
export function useSaya(aktif = true) {
  return useQuery({ queryKey: ['saya'], queryFn: () => api.get('/dashboard/me'), enabled: aktif })
}

/** Operator Smova mengisi namanya sendiri (ADR-107 no. 17). `null` mengosongkannya. */
export function useUbahNamaSendiri() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (name) => api.patch('/dashboard/me/nama', { name }),
    onSuccess: (data) => qc.setQueryData(['saya'], data),
  })
}

export function useModelTersedia() {
  return useQuery({
    queryKey: ['ta', 'model', 'tersedia'],
    queryFn: () => api.get('/dashboard/model/tersedia'),
    staleTime: 60_000,
  })
}

/** Mengganti seluruh daftar peran itu — urutannya adalah rantai cadangan. */
export function usePilihModel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ role, model_ids }) => api.put(`/dashboard/model/${role}`, { model_ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ta', 'model'] }),
  })
}

/** Field profil yang diatur tenant (ADR-063). Seluruhnya, termasuk yang nonaktif —
 *  yang nonaktif tetap punya isian dan tetap harus terlihat. */
export function useFieldProfil() {
  return useQuery({ queryKey: ['ta', 'field'], queryFn: () => api.get('/dashboard/field-profil') })
}

export function useTambahFieldProfil() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post('/dashboard/field-profil', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ta', 'field'] }),
  })
}

/** `kunci` TIDAK dikirim di badan — ia identitas, bukan isian (ADR-063). */
export function useUbahFieldProfil() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ kunci, ...sisa }) => api.patch(`/dashboard/field-profil/${kunci}`, sisa),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ta', 'field'] }),
  })
}

/** Setelan tenant SENDIRI — tanpa header tenant, karena tenant-nya hasil login. */
export function useSetelanSaya() {
  return useQuery({ queryKey: ['ta', 'setelan'], queryFn: () => api.get('/dashboard/settings') })
}

export function useUbahSetelanSaya() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.patch('/dashboard/settings', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ta', 'setelan'] }),
  })
}

/** Dokumen pengetahuan satu tenant — judul dan keadaan, BUKAN isinya. */
export function usePengetahuan(tenantId) {
  return useQuery({
    queryKey: ['adm', 'pengetahuan', tenantId],
    queryFn: () => api.get('/dashboard/pengetahuan', { tenant: tenantId }),
    enabled: !!tenantId,
  })
}

/** Jejak perubahan satu tenant, terbaru dulu. */
export function useAktivitas(tenantId, limit = 50) {
  return useQuery({
    queryKey: ['adm', 'aktivitas', tenantId, limit],
    queryFn: () => api.get(`/dashboard/aktivitas?limit=${limit}`, { tenant: tenantId }),
    enabled: !!tenantId,
  })
}

/** Fitur satu tenant, dilihat operator. `smova_admin` memenuhi peran apa pun,
 *  jadi rute `tenant_admin` bisa dipanggil dengan menyebut tenantnya. */
export function useFiturTenantOleh(tenantId) {
  return useQuery({
    queryKey: ['adm', 'fitur', tenantId],
    queryFn: () => api.get('/dashboard/fitur', { tenant: tenantId }),
    enabled: !!tenantId,
  })
}

/** Kesehatan layanan + operator Smova. Seluruhnya dari database yang sudah dipegang —
 *  tidak ada panggilan keluar, jadi membukanya tidak membakar kuota siapa pun. */
export function useKesehatan() {
  return useQuery({
    queryKey: ['adm', 'kesehatan'],
    queryFn: () => api.get('/sistem/kesehatan'),
    staleTime: 10_000,
  })
}

/** Peta kapabilitas per platform — termasuk yang belum punya adapter.
 *  Dibaca dari registri adapter di backend, jadi ia tidak bisa basi. */
/** Template pesan milik tenant — hari ini baru `chatbot_di_luar_jam`. */
export function useTemplate(kunci) {
  return useQuery({
    queryKey: ['ta', 'template', kunci],
    queryFn: () => api.get(`/dashboard/template/${kunci}`),
    enabled: Boolean(kunci),
  })
}

export function useSimpanTemplate(kunci) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.put(`/dashboard/template/${kunci}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ta', 'template', kunci] }),
  })
}

/** Akun platform & grup Telegram milik tenant sendiri — dibaca saja. */
export function useSambunganSaya() {
  return useQuery({
    queryKey: ['ta', 'sambungan'],
    queryFn: () => api.get('/dashboard/sambungan'),
  })
}

/* ── Persona bot & syarat meeting (migrasi 0030) ──────────────────────────── */

export function usePersona() {
  return useQuery({ queryKey: ['ta', 'persona'], queryFn: () => api.get('/dashboard/persona') })
}

export function useSimpanPersona() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.put('/dashboard/persona', body),
    // Pratinjau dirakit backend dari nilai yang BARU tersimpan — tanpa invalidasi, layar
    // menampilkan kalimat lama di sebelah pilihan baru, dan yang membaca tidak tahu mana
    // yang berlaku.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ta', 'persona'] }),
  })
}

/** Satu kartu persona: hanya medan yang dikirim yang berubah (ADR-094 no. 2). */
export function useUbahPersona() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.patch('/dashboard/persona', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ta', 'persona'] }),
  })
}

export function useSetelanMeeting() {
  return useQuery({
    queryKey: ['ta', 'meeting-setelan'],
    queryFn: () => api.get('/dashboard/meeting-setelan'),
  })
}

/**
 * "Yang ditawarkan bot sekarang" di Aturan meeting (ADR-124): dihitung server dengan fungsi
 * yang sama dengan bot. Ikut disegarkan tiap kali jadwal, setelan, atau libur disimpan.
 */
export function usePratinjauMeeting() {
  return useQuery({
    queryKey: ['ta', 'meeting-pratinjau'],
    queryFn: () => api.get('/dashboard/meeting-pratinjau'),
  })
}

const segarkanMeeting = (qc, kunci) => {
  qc.invalidateQueries({ queryKey: ['ta', kunci] })
  qc.invalidateQueries({ queryKey: ['ta', 'meeting-pratinjau'] })
}

export function useSimpanSetelanMeeting() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.put('/dashboard/meeting-setelan', body),
    onSuccess: () => segarkanMeeting(qc, 'meeting-setelan'),
  })
}

/** Kartu Aturan meeting: medan yang tidak dikirim tidak kembali ke bawaannya. */
export function useUbahSetelanMeeting() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.patch('/dashboard/meeting-setelan', body),
    onSuccess: () => segarkanMeeting(qc, 'meeting-setelan'),
  })
}

export function useKredensial(tenantId) {
  return useQuery({
    queryKey: ['adm', 'kredensial', tenantId],
    queryFn: () => api.get(`/tenants/${tenantId}/kredensial`),
    enabled: Boolean(tenantId),
  })
}

/**
 * Tanya platformnya siapa akun ini — tanpa efek samping.
 *
 * Menyimpan token dan token yang BEKERJA adalah dua hal berbeda. Tanpa ini, bedanya baru
 * ketahuan saat ada konten jatuh tempo lalu gagal terbit — berjam-jam sesudah orang yang
 * memasangnya pergi.
 */
/**
 * Tukar token Pengguna berumur pendek jadi token Halaman panjang-umur.
 *
 * Penukarannya di SERVER karena ia menuntut app_secret — melakukannya di peramban
 * berarti rahasia itu masuk ke URL dan riwayat peramban.
 */
export function useTukarTokenMeta(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post(`/tenants/${tenantId}/tukar-token-meta`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adm', 'kredensial', tenantId] })
      qc.invalidateQueries({ queryKey: ['adm', 'platform', tenantId] })
      qc.invalidateQueries({ queryKey: kunci.koneksi(tenantId) })
    },
  })
}

export function useUjiKoneksi(tenantId) {
  return useMutation({
    mutationFn: (platform) => api.post(`/tenants/${tenantId}/uji-koneksi/${platform}`),
  })
}

export function usePeta(tenantId) {
  return useQuery({
    queryKey: ['adm', 'platform', tenantId],
    queryFn: () => api.get(`/tenants/${tenantId}/platform`),
    enabled: !!tenantId,
  })
}

export function useGrup(tenantId) {
  return useQuery({
    queryKey: ['adm', 'grup', tenantId],
    queryFn: () => api.get(`/tenants/${tenantId}/grup`),
    enabled: !!tenantId,
  })
}

export function useTambahGrup(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post(`/tenants/${tenantId}/grup`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adm', 'grup', tenantId] }),
  })
}

export function useUbahGrup(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, aktif }) => api.patch(`/tenants/${tenantId}/grup/${id}?aktif=${aktif}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adm', 'grup', tenantId] }),
  })
}

/** Tempel atau cabut tautan undangan grup Telegram (ADR-121), hanya operator. */
export function useSimpanTautanGrup(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, tautan }) => api.put(`/tenants/${tenantId}/grup/${id}/tautan`, { tautan }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adm', 'grup', tenantId] }),
  })
}

/** Tautan grup Telegram tim dan apakah saya sudah tertaut (ADR-121), untuk semua peran tenant. */
export function useGabungGrup(aktif = true) {
  return useQuery({ queryKey: ['ta', 'gabung-grup'], queryFn: () => api.get('/dashboard/gabung-grup'), enabled: aktif })
}

/** Jadwal ketiga kelompok fitur (chatbot · meeting · content). */
export function useJadwal() {
  return useQuery({ queryKey: ['ta', 'jadwal'], queryFn: () => api.get('/dashboard/jadwal') })
}

export function useSimpanJadwal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, ...sisa }) => api.put(`/dashboard/jadwal/${key}`, sisa),
    onSuccess: () => segarkanMeeting(qc, 'jadwal'),
  })
}

export function useLibur() {
  return useQuery({ queryKey: ['ta', 'libur'], queryFn: () => api.get('/dashboard/libur') })
}

export function useTambahLibur() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post('/dashboard/libur', body),
    onSuccess: () => segarkanMeeting(qc, 'libur'),
  })
}

export function useHapusLibur() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.del(`/dashboard/libur/${id}`),
    onSuccess: () => segarkanMeeting(qc, 'libur'),
  })
}

/** Dokumen pengetahuan tenant SENDIRI — tanpa header tenant. */
/** Lima angka Beranda admin tenant — pengawasan, bukan penanganan. */
/** Unggah satu dokumen pengetahuan. Backend memenggal dan meng-embed langsung. */
export function useUnggahPengetahuan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (berkas) => api.unggah('/dashboard/pengetahuan', berkas),
    // Daftar dokumen DAN lencana menu ikut basi: keduanya membaca jumlah dokumen aktif.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ta', 'pengetahuan'] }),
  })
}

export function useRingkasanSaya(hari = 7) {
  return useQuery({
    queryKey: ['ta', 'ringkasan', hari],
    queryFn: () => api.get(`/dashboard/ringkasan?hari=${hari}`),
    staleTime: 60_000,
  })
}

/**
 * `aktif: false` menahan permintaannya (kartu Slot terbit dan kartu arah konten buatan sendiri
 * hanya butuh daftar ini kalau arah yang membaca Pengetahuan dipakai). `enabled` TIDAK disebut
 * selagi aktif: bawaan klien kuerinya yang berlaku, seperti sebelum pilihan ini ada.
 */
export function usePengetahuanSaya({ aktif = true } = {}) {
  return useQuery({
    queryKey: ['ta', 'pengetahuan'],
    queryFn: () => api.get('/dashboard/pengetahuan'),
    ...(aktif ? {} : { enabled: false }),
  })
}

/** Saklar "dipakai bot" satu dokumen. Dokumen yang mati tidak dibaca bot; isinya tetap ada. */
export function useSaklarDokumen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, is_active }) => api.patch(`/dashboard/pengetahuan/${id}`, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ta', 'pengetahuan'] }),
  })
}

/**
 * Hapus satu dokumen pengetahuan permanen, beserta potongannya (operator 14 Okt 2026). Kalau
 * namanya dilepas dari referensi iklan, daftar referensi konten ikut dimuat ulang.
 */
export function useHapusDokumen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.del(`/dashboard/pengetahuan/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ta', 'pengetahuan'] }); qc.invalidateQueries({ queryKey: ['konten', 'prompt-template'] }) },
  })
}

/** Pemakaian tenant sendiri. */
export function usePemakaianSaya(days = 30) {
  return useQuery({
    queryKey: ['ta', 'pemakaian', days],
    queryFn: () => api.get(`/dashboard/usage?days=${days}`),
  })
}

/** Orang di tenant SENDIRI — tanpa header tenant. */
export function useOrangSaya() {
  return useQuery({
    queryKey: ['ta', 'orang'],
    queryFn: () => api.get('/dashboard/web-users'),
  })
}

/**
 * Ganti akun sebuah platform: matikan yang lama, daftarkan yang baru, rotasi tokennya —
 * SATU perbuatan.
 *
 * Sampai 14 September ini menuntut empat langkah di dua layar, dan langkah keduanya
 * memasang jebakan: formulir "Tambah akun" menyembunyikan medan token dengan kalimat
 * "token sudah ada" — benar secara harfiah, menyesatkan secara praktis, karena token
 * yang ada milik akun LAMA.
 */
export function useGantiAkun(tenantId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post(`/tenants/${tenantId}/akun/ganti`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adm', 'platform', tenantId] })
      qc.invalidateQueries({ queryKey: ['adm', 'koneksi', tenantId] })
      qc.invalidateQueries({ queryKey: ['adm', 'kredensial', tenantId] })
    },
  })
}
